import image1 from '../assets/desserts/torta-1.jpg';
import image2 from '../assets/desserts/torta-2.jpg';
import image3 from '../assets/desserts/torta-3.jpg';
import image4 from '../assets/desserts/torta-4.jpg';
import image5 from '../assets/desserts/torta-5.jpg';
import image6 from '../assets/desserts/torta-6.jpg';

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguageContext } from '../context/languageContext';

const galleryImages = [image1, image2, image3, image4, image5, image6];
const SLIDE_MS = 5000;

export default function Gallery() {
  const { t } = useLanguageContext();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(
    () => setCurrentIndex((prev) => (prev + 1) % galleryImages.length),
    []
  );
  const prev = () =>
    setCurrentIndex((p) => (p - 1 + galleryImages.length) % galleryImages.length);

  useEffect(() => {
    if (paused) return;
    // Hold still for anyone who has asked the OS to reduce motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = setInterval(next, SLIDE_MS);
    return () => clearInterval(id);
  }, [paused, next]);

  return (
    <section id="gallery" className="py-12 sm:py-20 bg-cream">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header — same rhythm as the Menu and Custom Cakes sections */}
        <div className="text-center mb-8 md:mb-16">
          <div className="inline-block px-4 py-1.5 border border-gold/30 rounded-full mb-4">
            <span className="text-xs font-bold text-gold-deep tracking-widest uppercase">
              {t('Our Gallery')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-dark-chocolate mb-3 sm:mb-6 px-2">
            {t('Sweet')} <span className="italic gradient-text-gold-deep">{t('Moments')}</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto px-4 font-light text-sm sm:text-lg">
            {t('A glimpse of the cakes and pastries we craft every day.')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div
            className="relative rounded-xl overflow-hidden shadow-xl ring-1 ring-gold-deep/20"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            aria-roledescription="carousel"
            aria-label={t('Our Gallery')}
          >
            <div className="relative h-[320px] sm:h-[440px] md:h-[600px] bg-black/40">
              {galleryImages.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`Pasticeri Cialda — ${index + 1}/${galleryImages.length}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  aria-hidden={index !== currentIndex}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
                    index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                />
              ))}

              {/* Keeps the arrows and counter legible over bright frosting */}
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-transparent to-espresso/20 pointer-events-none" />

              <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-espresso/85 backdrop-blur-sm border border-gold/30 text-gold text-xs font-bold tracking-widest">
                {currentIndex + 1} / {galleryImages.length}
              </div>
            </div>

            <button
              onClick={prev}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-gold text-dark-chocolate p-3 md:p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
              aria-label={t('Previous')}
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <button
              onClick={next}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-gold text-dark-chocolate p-3 md:p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
              aria-label={t('Next')}
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Thumbnails double as the slide picker, so no separate dot row */}
          <div className="mt-6 md:mt-8 grid grid-cols-6 gap-2 md:gap-4">
            {galleryImages.map((src, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                aria-label={`${t('Go to slide')} ${index + 1}`}
                aria-current={index === currentIndex}
                className={`relative overflow-hidden rounded-lg aspect-square transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-deep ${
                  index === currentIndex
                    ? 'ring-2 ring-gold-deep opacity-100'
                    : 'opacity-50 hover:opacity-90'
                }`}
              >
                <img src={src} alt="" aria-hidden="true" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
